import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const STATUS_OPTIONS = [
  'Phone',
  'Email',
  'PAN done',
  'Other details done',
  'Address done',
  'Aadhar Done',
  'Payment done',
  'Nominee done',
  'VKYC done'
];

function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API_URL}/admin/dashboard`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setStats(statsRes.data.stats);
      setUsers(usersRes.data.users);
    } catch (error) {
      setError('Failed to fetch dashboard data');
      if (error.response?.status === 403 || error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (userId) => {
    try {
      const response = await axios.get(`${API_URL}/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetails(response.data);
      setSelectedUser(userId);
    } catch (error) {
      setError('Failed to fetch user details');
    }
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      setUpdatingStatus(leadId);
      const response = await axios.patch(
        `${API_URL}/leads/${leadId}/status`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update userDetails with the returned lead data
      if (userDetails) {
        setUserDetails({
          ...userDetails,
          leads: userDetails.leads.map(lead =>
            lead._id === leadId ? response.data.lead : lead
          )
        });
      }
      
      // Update selected lead
      if (selectedLead?._id === leadId) {
        setSelectedLead(response.data.lead);
      }
    } catch (error) {
      alert('Failed to update status: ' + (error.response?.data?.error || error.message));
    } finally {
      setUpdatingStatus(null);
    }
  };

  const addNote = async (leadId) => {
    if (!noteText.trim()) {
      alert('Please enter a note');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/leads/${leadId}/notes`,
        { note: noteText },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update userDetails with the returned lead data
      if (userDetails) {
        setUserDetails({
          ...userDetails,
          leads: userDetails.leads.map(lead =>
            lead._id === leadId ? response.data.lead : lead
          )
        });
      }
      
      // Update selected lead
      if (selectedLead?._id === leadId) {
        setSelectedLead(response.data.lead);
      }
      
      setNoteText('');
    } catch (error) {
      alert('Failed to add note: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const getEmailStatusBadgeClass = (status) => {
    return status === 'sent' 
      ? 'bg-green-100 text-green-800 border-green-200' 
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const getStatusBadgeClass = (status) => {
    const statusColors = {
      'Phone': 'bg-blue-100 text-blue-800',
      'Email': 'bg-indigo-100 text-indigo-800',
      'PAN done': 'bg-purple-100 text-purple-800',
      'Other details done': 'bg-pink-100 text-pink-800',
      'Address done': 'bg-orange-100 text-orange-800',
      'Aadhar Done': 'bg-yellow-100 text-yellow-800',
      'Payment done': 'bg-teal-100 text-teal-800',
      'Nominee done': 'bg-cyan-100 text-cyan-800',
      'VKYC done': 'bg-green-100 text-green-800'
    };
    return statusColors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-800">Master Admin Dashboard</h1>
          <button
            onClick={handleLogout}
            className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 transition duration-200"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Overall Statistics */}
        <div className="bg-white shadow rounded-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">Overall Statistics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
              <h3 className="text-sm font-medium text-blue-800">Total DSAs</h3>
              <p className="text-2xl font-bold">{stats?.totalDSAs || 0}</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg border border-green-100">
              <h3 className="text-sm font-medium text-green-800">Total Leads</h3>
              <p className="text-2xl font-bold">{stats?.totalLeads || 0}</p>
            </div>
            <div className="bg-teal-50 p-4 rounded-lg border border-teal-100">
              <h3 className="text-sm font-medium text-teal-800">Emails Sent</h3>
              <p className="text-2xl font-bold">{stats?.emailsSent || 0}</p>
            </div>
            <div className="bg-red-50 p-4 rounded-lg border border-red-100">
              <h3 className="text-sm font-medium text-red-800">Emails Failed</h3>
              <p className="text-2xl font-bold">{stats?.emailsFailed || 0}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* DSA Users List */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">DSA Users</h2>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {users.map((user) => (
                <div
                  key={user._id}
                  onClick={() => fetchUserDetails(user._id)}
                  className={`p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition ${
                    selectedUser === user._id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-medium text-gray-900">{user.name}</h3>
                      <p className="text-sm text-gray-600">{user.email}</p>
                      <p className="text-sm text-gray-600">{user.phone}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">
                        {user.stats.totalLeads} leads
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* User Details */}
          <div className="bg-white shadow rounded-lg p-6">
            <h2 className="text-xl font-semibold mb-4">
              {userDetails ? `${userDetails.user.name}'s Details` : 'Select a DSA'}
            </h2>
            
            {!userDetails ? (
              <div className="text-center py-10 text-gray-500">
                <p>Click on a DSA user to view their details</p>
              </div>
            ) : (
              <div>
                {/* User Stats */}
                <div className="grid grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 p-3 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Total Leads</p>
                    <p className="text-xl font-bold">{userDetails.user.stats.totalLeads}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Emails Sent</p>
                    <p className="text-xl font-bold">
                      {userDetails.leads.filter(l => l.emailStatus === 'sent').length}
                    </p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg text-center">
                    <p className="text-sm text-gray-600">Emails Failed</p>
                    <p className="text-xl font-bold">
                      {userDetails.leads.filter(l => l.emailStatus === 'failed').length}
                    </p>
                  </div>
                </div>

                {/* User Leads */}
                <h3 className="font-medium mb-3">Leads</h3>
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {userDetails.leads.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No leads yet</p>
                  ) : (
                    userDetails.leads.map((lead) => (
                      <div key={lead._id} className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{lead.customerName || 'N/A'}</p>
                            <p className="text-xs text-gray-600">{lead.customerPhone}</p>
                            <p className="text-xs text-gray-600">{lead.customerEmail}</p>
                            <p className="text-xs text-gray-600 mt-1">{lead.product}</p>
                            <p className="text-xs text-blue-600 font-mono mt-1">{lead.referralCode}</p>
                          </div>
                          <div className="flex flex-col gap-1">
                            <span className={`px-2 py-1 text-xs rounded-full border ${getEmailStatusBadgeClass(lead.emailStatus)}`}>
                              {lead.emailStatus}
                            </span>
                            <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(lead.status || 'Phone')}`}>
                              {lead.status || 'Phone'}
                            </span>
                          </div>
                        </div>
                        
                        <button
                          onClick={() => setSelectedLead(lead)}
                          className="text-xs text-blue-600 hover:text-blue-800 mt-2"
                        >
                          View Full Details
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Lead Details Modal */}
      {selectedLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold">Lead Details</h3>
                <button
                  onClick={() => setSelectedLead(null)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600">Lead ID</p>
                  <p className="font-medium">{selectedLead._id}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Customer Name</p>
                  <p className="font-medium">{selectedLead.customerName || 'N/A'}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Phone</p>
                  <p className="font-medium">{selectedLead.customerPhone}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Email</p>
                  <p className="font-medium">{selectedLead.customerEmail}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Referral Code</p>
                  <p className="font-mono font-medium text-blue-600">{selectedLead.referralCode}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Application Link</p>
                  <p className="font-mono text-sm break-all text-blue-600">{selectedLead.uniqueLink}</p>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(selectedLead.uniqueLink);
                      alert('Link copied!');
                    }}
                    className="mt-2 text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                  >
                    Copy Link
                  </button>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Status</p>
                  <select
                    value={selectedLead.status || 'Phone'}
                    onChange={(e) => updateLeadStatus(selectedLead._id, e.target.value)}
                    disabled={updatingStatus === selectedLead._id}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded"
                  >
                    {STATUS_OPTIONS.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Email Status</p>
                  <span className={`inline-block mt-1 px-2 py-1 text-xs rounded-full border ${getEmailStatusBadgeClass(selectedLead.emailStatus)}`}>
                    {selectedLead.emailStatus}
                  </span>
                </div>

                {selectedLead.emailMessageId && (
                  <div>
                    <p className="text-sm text-gray-600">Email Message ID</p>
                    <p className="text-sm font-mono">{selectedLead.emailMessageId}</p>
                  </div>
                )}

                <div>
                  <p className="text-sm text-gray-600 mb-2">Notes</p>
                  <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
                    {selectedLead.notes && selectedLead.notes.length > 0 ? (
                      selectedLead.notes.map(note => (
                        <div key={note._id} className="bg-gray-50 p-3 rounded border">
                          <p className="text-sm">{note.text}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            By {note.addedBy} on {new Date(note.createdAt).toLocaleString()}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-gray-500">No notes yet</p>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={noteText}
                      onChange={(e) => setNoteText(e.target.value)}
                      placeholder="Add a note..."
                      className="flex-1 px-3 py-2 border border-gray-300 rounded"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          addNote(selectedLead._id);
                        }
                      }}
                    />
                    <button
                      onClick={() => addNote(selectedLead._id)}
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      Add Note
                    </button>
                  </div>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Created At</p>
                  <p className="font-medium">{new Date(selectedLead.createdAt).toLocaleString()}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-600">Last Updated</p>
                  <p className="font-medium">{new Date(selectedLead.updatedAt).toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;